/**
 * Client-side image downscaling before upload. A phone photo is 3–8 MB; after
 * this it is a few hundred KB, which keeps R2 storage (and the user's upload
 * time) small without any server-side image processing.
 */
const MAX_EDGE = 1600;
const QUALITY = 0.85;

export interface PreparedImage { file: File; width: number; height: number }

export type AttachmentKind = 'image' | 'video' | 'audio';
export interface Attachment { kind: AttachmentKind; file: File; width: number; height: number; durationMs: number | null; previewUrl: string }

/** Read a video/audio file's duration (and video dimensions) through a media element without uploading it. */
export function probeMedia(file: File, kind: 'video' | 'audio'): Promise<{ durationMs: number | null; width: number; height: number }> {
  return new Promise((resolve) => {
    const el = document.createElement(kind);
    const url = URL.createObjectURL(file);
    const done = (v: { durationMs: number | null; width: number; height: number }) => { URL.revokeObjectURL(url); resolve(v); };
    el.preload = 'metadata';
    el.onloadedmetadata = () => done({
      durationMs: Number.isFinite(el.duration) ? Math.round(el.duration * 1000) : null,
      width: kind === 'video' ? (el as HTMLVideoElement).videoWidth : 0,
      height: kind === 'video' ? (el as HTMLVideoElement).videoHeight : 0,
    });
    el.onerror = () => done({ durationMs: null, width: 0, height: 0 });
    el.src = url;
  });
}

export function kindOf(file: File): AttachmentKind | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return null;
}

/** Prepare any attachment: images are downscaled; video/audio are probed for duration and checked against the limits. */
export async function prepareAttachment(file: File, limits: { maxBytes: number; maxDurationMs?: number }): Promise<Attachment> {
  const kind = kindOf(file);
  if (!kind) throw new Error(`Unsupported file: ${file.name}`);
  if (kind === 'image') {
    const img = await prepareImage(file);
    return { kind, file: img.file, width: img.width, height: img.height, durationMs: null, previewUrl: URL.createObjectURL(img.file) };
  }
  if (file.size > limits.maxBytes) throw new Error(`${file.name} is ${(file.size / 1024 / 1024).toFixed(0)} MB; the limit is ${Math.round(limits.maxBytes / 1024 / 1024)} MB`);
  const meta = await probeMedia(file, kind);
  if (limits.maxDurationMs && meta.durationMs !== null && meta.durationMs > limits.maxDurationMs) {
    throw new Error(`${file.name} is ${Math.round(meta.durationMs / 1000)}s; the limit is ${Math.round(limits.maxDurationMs / 1000)}s`);
  }
  return { kind, file, width: meta.width, height: meta.height, durationMs: meta.durationMs, previewUrl: URL.createObjectURL(file) };
}

export const fmtDuration = (ms: number | null) => ms === null ? '' : `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

export async function prepareImage(file: File): Promise<PreparedImage> {
  if (!file.type.startsWith('image/')) throw new Error('Not an image');
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return { file, width: 0, height: 0 }; // unsupported format (e.g. HEIC on some browsers): send as-is
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  if (scale === 1 && file.type === 'image/jpeg' && file.size < 600 * 1024) return { file, width: w, height: h };
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', QUALITY));
  if (!blob) return { file, width: w, height: h };
  return { file: new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }), width: w, height: h };
}
